import { LinkOutlined } from '@ant-design/icons';
import { CallAdminLinkUserPhoneExistingLink, CallAdminLinkUserPhoneRequest } from '@gooduncles/gu-app-schema';
import { Alert, Button, Descriptions, Form, Input, Modal, Segmented, notification } from 'antd';
import { FC, useState } from 'react';

import { useAuth } from 'src/stores/auth-context';

import { parseIfObject } from 'src/utils/common';
import { callAdminLinkUserPhone } from 'src/utils/firebase/firebase-callable';
import { AuditLogger } from 'src/utils/audit-logger';

const auditLogger = AuditLogger.getInstance();

type Mode = 'link' | 'unlink';

interface FormValues {
  phoneNumber: string;
  reason?: string;
}

interface LinkPhoneButtonProps {
  userId: string;
  defaultPhone?: string;
  userLabel?: string;
  /** 대상 사용자의 Firestore phoneLinked. false/undefined면 unlink 옵션을 비활성화한다. */
  phoneLinked?: boolean;
}

const PHONE_INPUT_PATTERN = /^[\d\s\-+()]+$/;

const LinkPhoneButton: FC<LinkPhoneButtonProps> = ({ userId, defaultPhone, userLabel, phoneLinked }) => {
  const { user: operator } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<Mode>('link');
  const [existingLink, setExistingLink] = useState<CallAdminLinkUserPhoneExistingLink | null>(null);
  const [form] = Form.useForm<FormValues>();

  const canUse = operator?.role === 'admin' || operator?.role === 'manager';
  if (!canUse) {
    return null;
  }

  const canUnlink = phoneLinked === true;
  const modeOptions = [
    { label: '연동', value: 'link' as const },
    {
      label: '해제',
      value: 'unlink' as const,
      disabled: !canUnlink,
    },
  ];

  const closeModal = () => {
    setOpen(false);
    setExistingLink(null);
    setMode('link');
    setLoading(false);
    form.resetFields();
  };

  const onModeChange = (next: Mode) => {
    if (loading) return;
    if (next === 'unlink' && !canUnlink) return;
    setMode(next);
    setExistingLink(null);
  };

  const runLink = async (values: FormValues, transferFromUid?: string) => {
    const phoneNumber = values.phoneNumber.trim();
    const reason = values.reason?.trim() || undefined;
    setLoading(true);

    try {
      const payload: CallAdminLinkUserPhoneRequest = {
        uid: userId,
        action: 'link',
        phoneNumber,
        transferFromUid,
        reason,
      };
      const { data } = await callAdminLinkUserPhone(payload);

      if (data.result === 'success') {
        const isTransfer = Boolean(data.transferred);
        notification.success({
          message: isTransfer ? '전화번호 이전 완료' : '전화번호 연동 완료',
          description: `${userLabel ?? userId}\n${data.phoneNumber ?? phoneNumber}`,
          style: { whiteSpace: 'pre-line' },
        });
        auditLogger.log({
          action: 'UPDATE',
          entity: 'user',
          entityId: userId,
          summary: `관리자 전화번호 ${isTransfer ? '이전' : '연동'} - ${userLabel ?? userId}, phone=${
            data.phoneNumber ?? phoneNumber
          }${reason ? `, reason=${reason}` : ''}`,
          changes: [{ field: 'phoneNumber', before: undefined, after: data.phoneNumber ?? phoneNumber }],
          metadata: {
            transferred: isTransfer,
            transferFromUid: transferFromUid ?? null,
            reason: reason ?? null,
          },
          slack: isTransfer ? { channel: '4-앱-알림' } : undefined,
        });
        closeModal();
        return;
      }

      if (data.errorCode === 'PHONE_ALREADY_LINKED' && data.existingLink) {
        setExistingLink(data.existingLink);
        auditLogger.log({
          action: 'UPDATE',
          entity: 'user',
          entityId: userId,
          summary: `관리자 전화번호 연동 - 기존 연동 발견(oldUid=${data.existingLink.uid}, email=${
            data.existingLink.email ?? '-'
          }), phone=${phoneNumber}`,
        });
        return;
      }

      const reasonText = data.reason ?? '알 수 없는 에러가 발생했습니다.';
      notification.error({
        message: `전화번호 연동 실패 (${data.errorCode ?? 'UNKNOWN'})`,
        description: reasonText,
      });
      auditLogger.log({
        action: 'UPDATE',
        entity: 'user',
        entityId: userId,
        summary: `관리자 전화번호 연동 실패(${data.errorCode ?? 'UNKNOWN'}) - phone=${phoneNumber}, ${reasonText}`,
        level: 'error',
        metadata: { transferFromUid: transferFromUid ?? null },
      });
    } catch (error) {
      console.error(error);
      const reasonText = parseIfObject(error);
      notification.error({
        message: '전화번호 연동 실패',
        description: reasonText,
      });
      auditLogger.log({
        action: 'UPDATE',
        entity: 'user',
        entityId: userId,
        summary: `관리자 전화번호 연동 실패 - phone=${phoneNumber}, ${reasonText}`,
        level: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const runUnlink = async (values: FormValues) => {
    const reason = values.reason?.trim() || undefined;
    setLoading(true);

    try {
      const payload: CallAdminLinkUserPhoneRequest = {
        uid: userId,
        action: 'unlink',
        reason,
      };
      const { data } = await callAdminLinkUserPhone(payload);

      if (data.result === 'success') {
        const wasLinked = Boolean(data.wasLinked);
        notification.success({
          message: wasLinked ? '전화번호 해제 완료' : '연동된 번호 없음 (no-op)',
          description: `${userLabel ?? userId}${data.phoneNumber ? `\n해제된 번호: ${data.phoneNumber}` : ''}`,
          style: { whiteSpace: 'pre-line' },
        });
        auditLogger.log({
          action: 'UPDATE',
          entity: 'user',
          entityId: userId,
          summary: `관리자 전화번호 해제${wasLinked ? '' : '/no-op'} - ${userLabel ?? userId}${
            data.phoneNumber ? `, phone=${data.phoneNumber}` : ''
          }${reason ? `, reason=${reason}` : ''}`,
          changes: wasLinked ? [{ field: 'phoneNumber', before: data.phoneNumber ?? null, after: null }] : undefined,
          metadata: {
            wasLinked,
            reason: reason ?? null,
          },
          slack: wasLinked ? { channel: '4-앱-알림' } : undefined,
        });
        closeModal();
        return;
      }

      const reasonText = data.reason ?? '알 수 없는 에러가 발생했습니다.';
      notification.error({
        message: `전화번호 해제 실패 (${data.errorCode ?? 'UNKNOWN'})`,
        description: reasonText,
      });
      auditLogger.log({
        action: 'UPDATE',
        entity: 'user',
        entityId: userId,
        summary: `관리자 전화번호 해제 실패(${data.errorCode ?? 'UNKNOWN'}) - ${reasonText}`,
        level: 'error',
      });
    } catch (error) {
      console.error(error);
      const reasonText = parseIfObject(error);
      notification.error({
        message: '전화번호 해제 실패',
        description: reasonText,
      });
      auditLogger.log({
        action: 'UPDATE',
        entity: 'user',
        entityId: userId,
        summary: `관리자 전화번호 해제 실패 - ${reasonText}`,
        level: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const onPrimarySubmit = async () => {
    const values = await form.validateFields();
    if (mode === 'link') {
      await runLink(values);
      return;
    }
    Modal.confirm({
      title: '전화번호 연동을 해제하시겠습니까?',
      content: `대상 계정(${
        userLabel ?? userId
      })의 Firebase Auth 전화번호 연동이 해제됩니다. 사용자는 더 이상 해당 번호로 로그인할 수 없습니다.`,
      okText: '해제 진행',
      okType: 'danger',
      cancelText: '취소',
      onOk: () => runUnlink(values),
    });
  };

  const onConfirmTransfer = async () => {
    if (!existingLink) return;
    const values = await form.validateFields();
    Modal.confirm({
      title: '기존 연동을 해제하고 이전하시겠습니까?',
      content: `기존 계정(${existingLink.email ?? existingLink.uid})에서 전화번호가 해제되고, 현재 계정(${
        userLabel ?? userId
      })에 연동됩니다. 자동으로 되돌릴 수 없습니다.`,
      okText: '이전 진행',
      okType: 'danger',
      cancelText: '취소',
      onOk: () => runLink(values, existingLink.uid),
    });
  };

  const primaryLabel = mode === 'link' ? '연동' : '해제';
  const primaryDanger = mode === 'unlink';

  return (
    <>
      <Button size='small' type='link' icon={<LinkOutlined />} onClick={() => setOpen(true)}>
        전화번호 관리
      </Button>
      <Modal
        open={open}
        title='관리자 전화번호 관리'
        onCancel={closeModal}
        maskClosable={!loading}
        closable={!loading}
        footer={
          existingLink
            ? [
                <Button key='cancel' onClick={closeModal} disabled={loading}>
                  취소
                </Button>,
                <Button key='transfer' danger type='primary' loading={loading} onClick={onConfirmTransfer}>
                  기존 연동 해제 후 이전
                </Button>,
              ]
            : [
                <Button key='cancel' onClick={closeModal} disabled={loading}>
                  취소
                </Button>,
                <Button key='submit' type='primary' danger={primaryDanger} loading={loading} onClick={onPrimarySubmit}>
                  {primaryLabel}
                </Button>,
              ]
        }
      >
        <Segmented
          value={mode}
          onChange={(v) => onModeChange(v as Mode)}
          options={modeOptions}
          disabled={loading || !!existingLink}
          block
          style={{ marginBottom: 16 }}
        />
        {!canUnlink && (
          <Alert type='info' showIcon style={{ marginBottom: 16 }} message='연동된 번호가 없어 해제는 불가합니다' />
        )}

        <Alert
          type={mode === 'unlink' ? 'error' : 'warning'}
          showIcon
          style={{ marginBottom: 16, whiteSpace: 'pre-line' }}
          message={mode === 'link' ? 'SMS 인증 없이 강제 연동합니다' : '대상 계정의 전화번호 연동을 해제합니다'}
          description={`대상 계정: ${userLabel ?? userId}\nuid: ${userId}\n반드시 본인 확인 절차를 거친 뒤 사용하세요.`}
        />

        <Form
          form={form}
          layout='vertical'
          initialValues={{ phoneNumber: defaultPhone ?? '', reason: 'v2 업데이트' }}
          disabled={loading}
        >
          {mode === 'link' && (
            <Form.Item
              label='전화번호'
              name='phoneNumber'
              rules={[
                { required: true, message: '전화번호를 입력하세요.' },
                { pattern: PHONE_INPUT_PATTERN, message: '숫자/공백/하이픈/괄호/+ 만 사용 가능합니다.' },
              ]}
            >
              <Input placeholder='010-1234-5678' disabled={!!existingLink || loading} />
            </Form.Item>
          )}
          <Form.Item label='CS 사유 (감사 로그에 기록)' name='reason'>
            <Input.TextArea
              rows={2}
              placeholder='예: 고객이 단말 분실로 인증 불가, 매장 전화로 본인 확인 완료'
              disabled={!!existingLink || loading}
            />
          </Form.Item>
        </Form>

        {existingLink && (
          <Alert
            type='error'
            showIcon
            style={{ marginTop: 8 }}
            message='이미 다른 계정에 연동된 번호입니다'
            description={
              <Descriptions column={1} size='small' style={{ marginTop: 8 }}>
                <Descriptions.Item label='uid'>{existingLink.uid}</Descriptions.Item>
                {existingLink.userName && <Descriptions.Item label='이름'>{existingLink.userName}</Descriptions.Item>}
                {existingLink.email && <Descriptions.Item label='이메일'>{existingLink.email}</Descriptions.Item>}
                {existingLink.storeId && <Descriptions.Item label='storeId'>{existingLink.storeId}</Descriptions.Item>}
              </Descriptions>
            }
          />
        )}
      </Modal>
    </>
  );
};

export default LinkPhoneButton;
